/* ONE CAMERA FOR EVERY ASSET (LS-199).
 *
 * Vijay: "this is fantastic i want to do this for every asset", and "let's use
 * the 3d pipeline to make all assets consistent perspective".
 *
 * The forager became a three-dimensional skeleton projected to pixels
 * (`rig3d.ts`) and the difference was not subtle, because the thing that had
 * been missing was not detail -- it was AGREEMENT. Every asset in this game
 * was drawn by hand in its own private idea of the view: a boulder painted as
 * a rectangle with a lit top edge (a player, with four arrows on a
 * screenshot: "what are these random ledges, they really mess with my sense
 * of depth perception"), a tree painted flat, a creature posed in screen
 * space. Any one of them looks fine alone. Together they are six different
 * cameras in one picture, and the eye reads that as wrongness it cannot name.
 *
 * So the camera lives here, once, and everything that wants to sit in the
 * world is built as a small solid in world space and projected through it:
 *
 *   screen x = x
 *   screen y = -height * HK + depth * DK
 *
 * HK is 1 and DK is a half. A thing one pixel tall rises one pixel on the
 * screen; a thing one pixel further away moves half a pixel up it. That is
 * the whole projection, it is the one `rig3d` uses, and using it everywhere
 * is the point of this module.
 *
 * WHAT COMES OUT IS PIXELS, NOT POLYGONS. A solid is rasterised the way the
 * rest of the art is: flat bands of palette colour with a hard one-pixel
 * outline, lit from the same quarter as everything else (up and to the left),
 * no anti-aliasing, no gradient. The geometry decides the SHAPE; the palette
 * and the banding decide the look, and the look is not this module's to
 * change.
 */
import { HK, DK } from './rig3d.ts';
export { HK, DK };

export type V3 = [number, number, number];

/** A solid: a sphere or a capsule, in world units, with its colours. */
export type Solid = { paintOnly?: boolean } & (
  | { kind: 'ball'; at: V3; r: number; rz?: number; ry?: number; skin: Skin }
  | { kind: 'rod'; a: V3; b: V3; r: number; skin: Skin }
  /* A BOX: `w` across, `h` tall, `d` deep, standing on `at`. The camera is
   * orthographic, so exactly two faces of it are ever visible -- the TOP,
   * which is `d * DK` tall on the screen, and the FRONT, which is `h * HK`.
   * That is what a cut step, a plinth course and a pushed stone are, and
   * having it here is what stops each of them being hand-painted again. */
  | { kind: 'box'; at: V3; w: number; h: number; d: number; skin: Skin });

/** How a solid takes the light: its own colour, the lit face and the shaded
 *  one. A solid with no `lit` is flat, which is right for a shadow. */
export type Skin = { base: string; lit?: string; dark?: string; edge?: string };

/* The sun: up and to the left, a little in front, as the tile art has always
 * had it. Used to decide which band of a solid is lit. */
export const SUN: V3 = [-0.45, 0.8, -0.4];

/** World -> screen. `at` is the solid's ground point in tile pixels. */
export function project(p: V3, ox: number, oy: number): [number, number] {
  return [ox + p[0], oy - p[1] * HK + p[2] * DK];
}

export type Px = (x: number, y: number, c: string) => void;

/** A tone mixed toward paper, for the shoulder of a solid whose palette gives
 *  a body colour and its shadow but no highlight. Kept here rather than
 *  imported so this module depends on nothing that depends on it. */
export function lighten(hex: string, k: number): string {
  // a palette with no such key hands over undefined; the caller's fallback
  // tone is the honest answer, and a throw here took four tile kinds down
  if (typeof hex !== 'string') return '#8a8298';
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const v = parseInt(m[1], 16);
  const mix = (c: number, w: number) => Math.round(c + (w - c) * k);
  const r = mix((v >> 16) & 255, 253), g2 = mix((v >> 8) & 255, 246), b2 = mix(v & 255, 230);
  return '#' + ((1 << 24) | (r << 16) | (g2 << 8) | b2).toString(16).slice(1);
}

/** The same, mixed toward black: the shadow tone for a mass whose outline
 *  should read as its own depth rather than as an ink line round it. Written
 *  for the thorn tile (LS-375), where thirteen stems each carrying a near-black
 *  edge on both sides filled the tile with a dark grid and the thicket read as
 *  a wooden crate. */
export function darken(hex: string, k: number): string {
  if (typeof hex !== 'string') return '#241a24';
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const v = parseInt(m[1], 16);
  const mix = (c: number) => Math.round(c * (1 - k));
  return '#' + ((1 << 24) | (mix((v >> 16) & 255) << 16) | (mix((v >> 8) & 255) << 8) | mix(v & 255)).toString(16).slice(1);
}

/** Draw one solid into a pixel sink, back to front by the caller. `ox`/`oy`
 *  are where the solid's origin sits on the canvas. */
export function paintSolid(s: Solid, ox: number, oy: number, put: Px) {
  if (s.kind === 'ball') ball(s, ox, oy, put);
  else if (s.kind === 'box') box(s, ox, oy, put);
  else rod(s, ox, oy, put);
}

/* A box, through the camera: the top face takes the sky, the front face takes
 * what is left, and the two are separated by the box's own front edge. */
function box(s: Extract<Solid, { kind: 'box' }>, ox: number, oy: number, put: Px) {
  const x0 = Math.round(ox + s.at[0] - s.w / 2), x1 = Math.round(ox + s.at[0] + s.w / 2) - 1;
  const nearTop = project([0, s.at[1] + s.h, s.at[2] + s.d / 2], 0, oy)[1];
  const farTop = project([0, s.at[1] + s.h, s.at[2] - s.d / 2], 0, oy)[1];
  const nearFoot = project([0, s.at[1], s.at[2] + s.d / 2], 0, oy)[1];
  const lit = s.skin.lit || s.skin.base;
  const dark = s.skin.dark || s.skin.base;
  for (let y = Math.round(farTop); y < Math.round(nearTop); y++) {
    for (let x = x0; x <= x1; x++) put(x, y, lit);                 // the tread
  }
  for (let y = Math.round(nearTop); y < Math.round(nearFoot); y++) {
    for (let x = x0; x <= x1; x++) put(x, y, s.skin.base);         // the riser
  }
  /* THE FAR EDGE IS A SHADOW. In this projection a box behind another one is
   * drawn first and then covered, so what separates two boxes standing in a
   * row -- the steps of a stair, the courses of a plinth -- is not their
   * risers, which the nearer box hides, but the shadow the nearer box throws
   * back onto the further one. One dark row at the far edge of the top face
   * is that shadow, and without it a flight of steps rasterises as one pale
   * slab with faint banding: reported twice, in those words. */
  for (let x = x0; x <= x1; x++) {
    put(x, Math.round(farTop), dark);
    put(x, Math.round(nearTop), s.skin.base);
    put(x, Math.round(nearFoot) - 1, dark);
  }
}

function shade(skin: Skin, t: number): string {
  // t is 1 at the lit shoulder, 0 at the shaded foot
  if (t > 0.66 && skin.lit) return skin.lit;
  if (t < 0.14 && skin.dark) return skin.dark;
  return skin.base;
}

/* A ball: an ellipsoid, drawn as rows. The vertical radius is the sphere's
 * radius times HK and the depth radius times DK, so a round rock is an
 * ellipse on the screen and its foot is where the ground is -- which is the
 * whole reason the boulder stopped reading as a step. */
function ball(s: Extract<Solid, { kind: 'ball' }>, ox: number, oy: number, put: Px) {
  const [cx, cy] = project(s.at, ox, oy);
  const rz = s.rz ?? s.r, rh = s.ry ?? s.r;
  // width, depth and HEIGHT all matter: a brazier's bowl is a shallow dish
  // and a boulder is a ball, and with one radius they came out the same
  const rx = s.r, ry = Math.max(1, rh * HK * 0.62 + rz * DK * 0.9);
  const y0 = Math.floor(cy - ry), y1 = Math.ceil(cy + ry);
  for (let y = y0; y <= y1; y++) {
    const v = (y + 0.5 - cy) / ry;
    if (Math.abs(v) > 1) continue;
    const hw = rx * Math.sqrt(Math.max(0, 1 - v * v));
    const x0 = Math.round(cx - hw), x1 = Math.round(cx + hw);
    for (let x = x0; x <= x1; x++) {
      const u = (x + 0.5 - cx) / rx;
      /* THE LIGHT COMES FROM UP AND TO THE LEFT, and that is the whole of the
       * shading: a cap of the lit tone on the shoulder facing the sun, the
       * body's own colour across the middle, the shaded tone along the foot.
       * A per-pixel normal was tried first and it striped the mass with ink
       * -- at four pixels of radius there are not enough pixels for a
       * gradient, and the bands ARE the style. */
      const t = 0.5 - u * 0.3 - v * 0.5;
      put(x, y, shade(s.skin, t));
    }
  }
}

/* A rod: a capsule between two world points, projected and drawn as a thick
 * line with round ends. Trunks, branches, limbs and rails are rods. */
function rod(s: Extract<Solid, { kind: 'rod' }>, ox: number, oy: number, put: Px) {
  const a = project(s.a, ox, oy), b = project(s.b, ox, oy);
  const r = s.r;
  const x0 = Math.floor(Math.min(a[0], b[0]) - r), x1 = Math.ceil(Math.max(a[0], b[0]) + r);
  const y0 = Math.floor(Math.min(a[1], b[1]) - r), y1 = Math.ceil(Math.max(a[1], b[1]) + r);
  const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1;
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
    const px = x + 0.5, py = y + 0.5;
    const t = Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / L2));
    const qx = a[0] + dx * t, qy = a[1] + dy * t;
    const d = Math.hypot(px - qx, py - qy);
    if (d > r) continue;
    // across the rod: the far side from the sun is its shaded edge
    const across = (px - qx) / r;
    const lit = (0.5 - across * 0.5 * Math.sign(SUN[0])) ;
    put(x, y, d > r - 0.8 && s.skin.edge ? s.skin.edge : shade(s.skin, lit));
  }
}

/** Depth order: what is nearer the camera is drawn later. A solid's depth is
 *  its ground point's `z` plus a little of its height, because a tall thing
 *  leans toward the viewer in this projection. */
export function depthOf(s: Solid): number {
  const p: V3 = s.kind === 'rod'
    ? [(s.a[0] + s.b[0]) / 2, (s.a[1] + s.b[1]) / 2, (s.a[2] + s.b[2]) / 2]
    : s.at;
  return p[2] + p[1] * 0.1;
}

/** Paint a scene of solids, back to front, into a pixel sink. */
export function paintScene(solids: Solid[], ox: number, oy: number, put: Px) {
  for (const s of [...solids].sort((p, q) => depthOf(p) - depthOf(q))) paintSolid(s, ox, oy, put);
}

/* A SILHOUETTE IS ONE OUTLINE, NOT ONE PER SOLID. Two overlapping balls each
 * drawing their own rim put ink through the middle of the mass and the rock
 * came out looking like a skull. So a scene rasterises into its own little
 * grid, and the ink is laid around what the grid ends up holding -- the
 * outline of the THING, which is what the rest of the art does too. */
export type Sheet = { w: number; h: number; px: (string | null)[] };

export function renderScene(w: number, h: number, solids: Solid[], ox: number, oy: number, ink?: string): Sheet {
  const sheet: Sheet = { w, h, px: new Array(w * h).fill(null) };
  const put: Px = (x, y, c) => { if (x >= 0 && y >= 0 && x < w && y < h) sheet.px[y * w + x] = c; };
  { const cap = (globalThis as any).__m3Capture; if (cap) cap.push({ kind: 'scene', w, h, ox, oy, solids }); }   // a probe listening for the solids themselves (the true-3D evaluation, 2026-09-13); inert otherwise
  paintScene(solids, ox, oy, put);
  if (ink) {
    const out = sheet.px.slice();
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (sheet.px[y * w + x]) continue;
      const n = (x > 0 && sheet.px[y * w + x - 1]) || (x < w - 1 && sheet.px[y * w + x + 1])
        || (y > 0 && sheet.px[(y - 1) * w + x]) || (y < h - 1 && sheet.px[(y + 1) * w + x]);
      if (n) out[y * w + x] = ink;
    }
    sheet.px = out;
  }
  return sheet;
}

/** Blit a rendered sheet as horizontal RUNS: one call per stretch of a colour
 *  along a row, rather than one per pixel. A wide flat face -- a ramp's
 *  tread, a wall's cap -- comes out as the single wide rectangle it is,
 *  which is both cheaper and what a reader of the draw calls expects to see. */
export function blitRuns(sheet: Sheet, rect: (x: number, y: number, w: number, c: string) => void) {
  for (let y = 0; y < sheet.h; y++) {
    let x = 0;
    while (x < sheet.w) {
      const c = sheet.px[y * sheet.w + x];
      if (!c) { x++; continue; }
      let x2 = x + 1;
      while (x2 < sheet.w && sheet.px[y * sheet.w + x2] === c) x2++;
      rect(x, y, x2 - x, c);
      x = x2;
    }
  }
}

/** Blit a rendered sheet through a pixel sink. */
export function blit(sheet: Sheet, put: Px) {
  for (let y = 0; y < sheet.h; y++) for (let x = 0; x < sheet.w; x++) {
    const c = sheet.px[y * sheet.w + x];
    if (c) put(x, y, c);
  }
}

/** The contact shadow a solid casts on flat ground: an ellipse at its foot,
 *  wider than the solid and half as deep, which is what a shadow is under a
 *  high sun in this projection. */
export function groundShadow(at: V3, r: number, ox: number, oy: number, put: Px, col: string) {
  const [cx, cy] = project([at[0], 0, at[2]], ox, oy);
  const rx = r * 1.05, ry = Math.max(1, r * DK * 1.1);
  /* A SHADOW'S EDGE IS NOT A CUT (2026-09-05, against the reference renders: "replace the hard shadow edges with a
   * two-or-three pixel dithered gradient"). The core is solid; the outer fifth is laid down on a checker and the very
   * rim on one pixel in four, which at this scale reads as a soft edge and stays palette art. */
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      const u = (x + 0.5 - cx) / rx, v = (y + 0.5 - cy) / ry;
      const d = u * u + v * v;
      if (d > 1.18) continue;
      if (d <= 0.72) put(x, y, col);
      else if (d <= 0.95) { if (((x + y) & 1) === 0) put(x, y, col); }
      else if (((x & 1) === 0 && (y & 1) === 0)) put(x, y, col);
    }
  }
}
